import "./ValidateSectionView.styl";
import { runWaffleValidate, getWaffleProgress, getWaffleIsValid, timediff } from "../_resources/waffle-helpers.js";

export default function ServerValidateSectionView({ dom, message, getToken }) {
  const { bad, info, progress, good } = message;
  const container = dom;

  const sectionHeader = container.append("div").class("admin-section-header");
  sectionHeader.append("h1").text("Validate Dataset");

  const controls = container.append("div").class("validate-controls");

  const datasetRow = controls.append("div").class("validate-row");
  datasetRow.append("label").class("label").text("Dataset");
  const datasetSelect = datasetRow.append("select").class("input validate-select");

  const branchRow = controls.append("div").class("validate-row");
  branchRow.append("label").class("label").text("Branch");
  const branchSelect = branchRow.append("select").class("input validate-select");

  const actionRow = controls.append("div").class("validate-row");
  const validateBtn = actionRow.append("button").class("button bigger").text("✅ Validate");

  const resultSection = container.append("div").class("validate-result");
  const resultStatus = resultSection.append("div").class("validate-result-status");
  const resultBody = resultSection.append("div").class("validate-result-body");

  // --- State set by render() ---
  let _selectedServerUrl = null;
  let _serverDatasets = [];

  // --- Helpers ---

  function getSelectedDataset() {
    return _serverDatasets.find(d => d.slug === datasetSelect.node().value);
  }

  function renderDatasetOptions() {
    datasetSelect.html("");
    _serverDatasets.forEach(ds => {
      datasetSelect.append("option").attr("value", ds.slug).text(ds.slug);
    });
  }

  function renderBranchOptions() {
    branchSelect.html("");
    const ds = getSelectedDataset();
    if (!ds) return;
    ds.branches.forEach(b => {
      const opt = branchSelect.append("option").attr("value", b).text(b);
      if (b === ds.default_branch) opt.attr("selected", true).text(b + " (default)");
    });
  }

  // --- Result rendering helpers ---

  function fileFragment(filePath, branch) {
    if (!filePath) return "";
    if (!branch) return filePath;
    const idx = filePath.indexOf("/" + branch + "/");
    return idx >= 0 ? filePath.slice(idx + branch.length + 2) : filePath;
  }

  function renderErrorTable(errors, branch) {
    resultBody.html("");
    const groups = new Map();
    for (const e of errors) {
      const key = e.errorCode || "(unknown)";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(e);
    }

    const sortedGroups = [...groups.entries()]
      .sort((a, b) => b[1].length - a[1].length);

    const table = resultBody.append("table").class("validate-table");
    for (const [code, items] of sortedGroups) {
      items.sort((a, b) => (a.lineNo ?? Infinity) - (b.lineNo ?? Infinity));
      table.append("tr").class("validate-group-header")
        .append("td").attr("colspan", 4)
        .html(`<span class="validate-code">${code}</span> <span class="validate-count">${items.length}</span>`);

      const PREVIEW = 10;
      const visible = items.slice(0, PREVIEW);
      const hidden = items.slice(PREVIEW);

      for (const item of visible) {
        const tr = table.append("tr")
          .class(item.isWarning ? "validate-row-warning" : "validate-row-error")
          .attr("title", item.suggestions || null);
        tr.append("td").class("validate-col-icon").text(item.isWarning ? "⚠️" : "❌");
        tr.append("td").class("validate-col-file").text(fileFragment(item.file, branch));
        tr.append("td").class("validate-col-lineno").text(item.lineNo ? item.lineNo + ":" : "");
        tr.append("td").class("validate-col-message").text(item.message);
      }

      if (hidden.length > 0) {
        const moreRow = table.append("tr").class("validate-row-more");
        const moreCell = moreRow.append("td").attr("colspan", 4);
        const moreBtn = moreCell.append("button").class("validate-more-btn")
          .text(`+ ${hidden.length} more`);
        moreBtn.on("click", () => {
          moreRow.remove();
          for (const item of hidden) {
            const tr = table.append("tr")
              .class(item.isWarning ? "validate-row-warning" : "validate-row-error")
              .attr("title", item.suggestions || null);
            tr.append("td").class("validate-col-icon").text(item.isWarning ? "⚠️" : "❌");
            tr.append("td").class("validate-col-file").text(fileFragment(item.file, branch));
            tr.append("td").class("validate-col-lineno").text(item.lineNo ? item.lineNo + ":" : "");
            tr.append("td").class("validate-col-message").text(item.message);
          }
        });
      }
    }
  }

  function renderResult(result, branch) {
    const isOk = Boolean(result.success);
    const errors = result.errors || [];
    const warnCount = errors.filter(e => e.isWarning).length;
    const errCount = errors.filter(e => !e.isWarning).length;

    const countLabel = errCount > 0
      ? `${errCount} error(s)${warnCount > 0 ? `, ${warnCount} warning(s)` : ""}`
      : warnCount > 0 ? `${warnCount} warning(s)` : "";

    const versionLabel = result.validatorVersion ? ` <span class="validate-version">${result.validatorVersion}</span>` : "";
    const timestampLabel = result.timestamp ? ` <span class="validate-timestamp" title="${result.timestamp}">${timediff(result.timestamp, false, true)}</span>` : "";
    resultStatus.html(
      isOk
        ? `<span class="validate-ok">✅ Valid${countLabel ? ` — ${countLabel}` : ""}</span>${versionLabel}${timestampLabel}`
        : `<span class="validate-fail">❌ Invalid — ${countLabel}</span>${versionLabel}${timestampLabel}`
    );

    if (errors.length > 0) renderErrorTable(errors, branch);
  }

  async function fetchCachedResult() {
    const slug = datasetSelect.node().value;
    const branch = branchSelect.node().value;
    if (!slug || !branch || !_selectedServerUrl) return;

    resultStatus.html(`<span class="validate-not-run">…</span>`);
    resultBody.html("");

    const { ok: result } = await getWaffleIsValid(_selectedServerUrl, slug, branch, getToken());
    if (!result || result.validated === false) {
      resultStatus.html("");
      return;
    }
    renderResult(result, branch);
  }

  // --- Events ---

  datasetSelect.on("change", () => { renderBranchOptions(); fetchCachedResult(); });

  branchSelect.on("change", () => fetchCachedResult());

  validateBtn.on("click", async () => {
    if (!_selectedServerUrl) return bad("No server selected");
    const slug = datasetSelect.node().value;
    const branch = branchSelect.node().value;
    if (!slug) return bad("No dataset selected");

    validateBtn.attr("disabled", true);
    resultStatus.html("");
    resultBody.html("");

    const { ok: validateResponse, error } = await runWaffleValidate(_selectedServerUrl, slug, branch, getToken());
    if (error) {
      validateBtn.attr("disabled", null);
      return bad("Failed to start validation: " + error);
    }

    progress("Validating…");

    let retries = 0;
    const interval = setInterval(async () => {
      const { ok: prog, error: progErr } = await getWaffleProgress(_selectedServerUrl);
      if (progErr || !prog) {
        retries++;
        if (retries > 5) {
          clearInterval(interval);
          validateBtn.attr("disabled", null);
          bad("Lost contact with server during validation");
        }
        return;
      }
      retries = 0;

      const latestEvent = prog.events?.at(-1)?.comment || "";
      progress(latestEvent || "Validating…");

      if (!prog.ongoing) {
        clearInterval(interval);
        validateBtn.attr("disabled", null);
        await showResult(slug, branch);
      }
    }, 500);
  });

  async function showResult(slug, branch) {
    const { ok: result, error } = await getWaffleIsValid(_selectedServerUrl, slug, branch, getToken());
    if (error) return bad("Failed to fetch validation result: " + error);

    if (!result || result.validated === false) {
      resultStatus.html(`<span class="validate-not-run">⚠️ No validation result stored yet</span>`);
      resultBody.html("").append("pre").class("validate-result-json").text(JSON.stringify(result, null, 2));
      info("No validation result stored for this dataset/branch");
      return;
    }

    renderResult(result, branch);

    const isOk = Boolean(result.success);
    const errors = result.errors || [];
    const warnCount = errors.filter(e => e.isWarning).length;
    const errCount = errors.filter(e => !e.isWarning).length;
    const countLabel = errCount > 0
      ? `${errCount} error(s)${warnCount > 0 ? `, ${warnCount} warning(s)` : ""}`
      : warnCount > 0 ? `${warnCount} warning(s)` : "";

    if (isOk) good(countLabel ? `Valid with ${countLabel}` : "Validation passed ✅", 4000);
    else bad(`Validation failed — ${countLabel}`);
  }

  // --- Main render: called by servers.js with server context ---

  function render({ selectedServerUrl, serverDatasets, token }) {
    _selectedServerUrl = selectedServerUrl;
    _serverDatasets = serverDatasets || [];

    sectionHeader.select("h1").text("Validate Dataset");
    controls.style("display", null);
    resultStatus.html("");
    resultBody.html("");
    renderDatasetOptions();
    renderBranchOptions();
    fetchCachedResult();
  }

  function renderError(message) {
    sectionHeader.select("h1").text(message);
    controls.style("display", "none");
    resultStatus.html("");
    resultBody.html("");
  }

  return { render, renderError };
}
